const BRAND_SUFFIX_RE = /\s\|\sJobcrawls$/i;

/** Trailing pagination fragment from +Head / +title (e.g. " (Page 2)"). */
const PAGE_SUFFIX_RE = /(\s+\((?:Page|Sivu|Sida)\s+\d+\))\s*$/;

/** En dash separating a trailing "N Open Positions"-style count clause from the rest of the title. */
const COUNT_CLAUSE_SEP = ' – ';

/**
 * Shared cap for every generated `<title>` across page types (SRP, homepage, curated overrides).
 * One number so there's a single place that decides what "too long" means, instead of each
 * page-type generator picking its own (SRP used to cap internally at 65 before this function's
 * own 60-char pass ran a second time on the result).
 */
export const DEFAULT_SEO_TITLE_MAX_LENGTH = 60;

function stripPageSuffix(title: string): { core: string; pageSuffix: string } {
  const m = title.match(PAGE_SUFFIX_RE);
  if (!m?.[1]) return { core: title, pageSuffix: '' };
  return { core: title.slice(0, -m[1].length).trimEnd(), pageSuffix: m[1] };
}

/** Cuts at the last " – " before `maxLength` when present, so a trailing count clause
 *  ("– 1,240 Open Positions") drops whole rather than getting chopped mid-word. */
function cutAtCountClause(text: string, maxLength: number): string | null {
  const dashIdx = text.lastIndexOf(COUNT_CLAUSE_SEP);
  if (dashIdx > 20 && dashIdx <= maxLength) return text.substring(0, dashIdx);
  return null;
}

function truncatePlain(title: string, maxLength: number): string {
  if (title.length <= maxLength) return title;

  const viaCountClause = cutAtCountClause(title, maxLength);
  if (viaCountClause !== null) return viaCountClause;

  const truncated = title.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');
  return lastSpace > 50 ? truncated.substring(0, lastSpace).trim() : `${truncated.trim()}...`;
}

/**
 * Truncates SEO titles (~60 chars) without dropping the final `| Jobcrawls` brand segment
 * when present. Optional ` (Page N)` / ` (Sivu N)` / ` (Sida N)` suffix is preserved after truncation.
 */
export function truncateSeoTitle(title: string, maxLength: number = DEFAULT_SEO_TITLE_MAX_LENGTH): string {
  const { core, pageSuffix } = stripPageSuffix(title);
  const budget = maxLength - pageSuffix.length;

  if (core.length <= budget) {
    return core + pageSuffix;
  }

  const brandMatch = core.match(BRAND_SUFFIX_RE);
  if (!brandMatch) {
    return truncatePlain(core, budget) + pageSuffix;
  }

  const brandSuffix = brandMatch[0];
  const head = core.slice(0, -brandSuffix.length);

  if (head.length + brandSuffix.length <= budget) {
    return core + pageSuffix;
  }

  const headBudget = budget - brandSuffix.length;
  if (headBudget < 10) {
    return `${(head + brandSuffix).substring(0, budget).trim()}${pageSuffix}`;
  }

  const viaCountClause = cutAtCountClause(head, headBudget);
  if (viaCountClause !== null) {
    return viaCountClause + brandSuffix + pageSuffix;
  }

  const truncatedHead = head.substring(0, headBudget);
  const lastSpace = truncatedHead.lastIndexOf(' ');
  const shortened =
    lastSpace > Math.max(8, Math.floor(headBudget * 0.55))
      ? truncatedHead.substring(0, lastSpace).trim()
      : truncatedHead.trim();

  return shortened + brandSuffix + pageSuffix;
}
